import { useMemo } from 'react';
import { LocationFeatureSettings } from '@weave/schema-gen-ts/dist/schemas/payments/messages/location_settings/location_settings.pb';
import { PaymentsView1 } from '@frontend/api-payments-view';
import { PaymentsFeatureFlags, useSelectedLocations } from '@frontend/payments-hooks';
import { useScopedAppFlagStore } from '@frontend/scope';

/*
ORDER IS IMPORTANT.
IF YOU MOVE THE ORDER OF THE paymentsPmsDisplayNamePatterns, IT WILL AFFECT THE BEHAVIOUR OF THE CODE.

TODO:: Robust solution is to don't use the ragex for matching the pms name.
Instead BE schema will return the type as enum and we can use that to match the pms name.
*/
const paymentsPmsDisplayNamePatterns = {
  openDental: /\bOPENDENTAL\b/i,
  openDentalLocal: /OPENDENTAL_CLOUD_LOCAL/i,
  openDentalCloud: /OPENDENTAL_CLOUD/i,
  eaglesoft: /EAGLESOFT(_\d+)?/i,
  fuse: /PATTERSON_FUSE/i,
  drChrono: /DR_CHRONO/i,
  dentrixEnterprise: /DENTRIX_ENTERPRISE/i,
  dolphinCloud: /DOLPHIN_CLOUD/i, // Added Dolphin Cloud pattern
  dolphinLegacy: /DOLPHIN_LEGACY/i,
  dolphin: /DOLPHIN/i,
  crystal: /CRYSTAL/i,
  athenaOne: /ATHENAONE/i,
  nextechPractice: /NEXTECH_PRACTICE/i,
  nextechSelect: /NEXTECH/i,
} as const;

type DisplayNameAccessor = keyof typeof paymentsPmsDisplayNamePatterns;
export type PaymentsPmsEnabledLocations = Partial<Record<DisplayNameAccessor, string[]>>;
export type BulkCollectionPmsEnabledLocations = Partial<Record<DisplayNameAccessor, boolean>>;

export const sourceAndFeatureSettingsPmsKeyNameMapping = {
  openDental: 'openDentalWritebacksEnabled',
  openDentalCloud: 'openDentalCloudWritebacksEnabled',
  eaglesoft: 'eaglesoftWritebacksEnabled',
  fuse: 'fuseWritebacksEnabled',
  drChrono: 'drChronoWritebacksEnabled',
  dentrixEnterprise: 'dentrixEnterpriseWritebacksEnabled',
  dolphinCloud: 'dolphinWritebacksEnabled',
  openDentalLocal: 'openDentalLocalWritebacksEnabled',
  dolphin: 'dolphinWritebacksEnabled',
  crystal: 'crystalWritebacksEnabled',
  athenaOne: 'athenaOneWritebacksEnabled',
  nextechSelect: 'nextechSelectWritebacksEnabled',
  nextechPractice: 'nextechPracticeWritebacksEnabled',
  dolphinLegacy: 'dolphinLegacyWritebacksEnabled',
} as const satisfies Partial<Record<DisplayNameAccessor, keyof LocationFeatureSettings>>;

// Helper function to find PMS type from display name
export const findPmsTypeFromName = (pmsName: string): DisplayNameAccessor | null => {
  for (const [key, pattern] of Object.entries(paymentsPmsDisplayNamePatterns)) {
    if (pattern.test(pmsName)) {
      return key as DisplayNameAccessor;
    }
  }
  return null;
};

// Helper function to check if sync is completed
export const isSyncCompleted = (source: any): boolean => {
  return (
    source?.lastSyncStartedAt &&
    !isDefaultUTCDate(source.lastSyncStartedAt) &&
    source?.lastSyncCompletedAt &&
    !isDefaultUTCDate(source.lastSyncCompletedAt)
  );
};

export function isDefaultUTCDate(utcDate: string) {
  // Default UTC date is of the format "0001-01-01 00:00:00.0000 +0000 UTC"
  const defaultDate = /^0001-01-01 00:00:00\.?\d* ?[+|-]?\d* ?.*$/;

  // Default UTC date is of the format "0001-01-01T00:00:00Z"
  const alternateDefaultDate = /^0001-01-01T00:00:00Z$/;

  return defaultDate.test(utcDate) || alternateDefaultDate.test(utcDate);
}

export const useDataSources = () => {
  const { locationIds } = useSelectedLocations();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const enableCollectionFlow = getFeatureFlagValue(PaymentsFeatureFlags.DxeCollectionFlow);

  const { data: sources } = PaymentsView1.Queries.useGetLocationsSourceInfoQuery({
    request: { locationIds: locationIds.filter(Boolean) as string[] },
    options: { enabled: locationIds.length > 0 },
  });

  const { paymentsPmsEnabledLocationsFromSources, bulkCollectionsEnabledLocationsFromSources } = useMemo(() => {
    const paymentsPmsEnabledLocationsFromSources: PaymentsPmsEnabledLocations = {};
    const bulkCollectionsEnabledLocationsFromSources: BulkCollectionPmsEnabledLocations = {};

    if (!sources?.locationsSourceInfo)
      return { paymentsPmsEnabledLocationsFromSources, bulkCollectionsEnabledLocationsFromSources };

    sources.locationsSourceInfo.forEach(({ locationId, sources: locationSources }) => {
      if (!locationId || !locationSources) return;

      locationSources.forEach((source) => {
        // Skip if sync is not completed or PMS name is missing
        if (!isSyncCompleted(source) || !source?.pmsName) return;

        const pmsType = findPmsTypeFromName(source.pmsName);
        if (!pmsType) return;

        // Initialize array if it doesn't exist
        if (!paymentsPmsEnabledLocationsFromSources[pmsType]) {
          paymentsPmsEnabledLocationsFromSources[pmsType] = [];
        }

        paymentsPmsEnabledLocationsFromSources[pmsType]!.push(locationId);

        if (enableCollectionFlow) {
          // Check for bulk collections eligibility
          const isBulkCollectionsEligible = source?.capability?.bulkCollectionEnabled;
          bulkCollectionsEnabledLocationsFromSources[pmsType] = !!isBulkCollectionsEligible;
        }
      });
    });

    return { paymentsPmsEnabledLocationsFromSources, bulkCollectionsEnabledLocationsFromSources };
  }, [sources]);

  // Helper function to safely get locations for a PMS type
  const getLocationsForPmsType = (pmsType: DisplayNameAccessor): string[] => {
    return paymentsPmsEnabledLocationsFromSources[pmsType] ?? [];
  };

  const drChronoWritebacksLocations = getLocationsForPmsType('drChrono');
  const openDentalWritebacksLocations = getLocationsForPmsType('openDental');
  const openDentalCloudWritebacksLocations = getLocationsForPmsType('openDentalCloud');
  const eaglesoftWritebacksLocations = getLocationsForPmsType('eaglesoft');
  const fuseWritebacksLocations = getLocationsForPmsType('fuse');
  const dentrixEnterpriseWritebacksLocations = getLocationsForPmsType('dentrixEnterprise');
  const dolphinCloudWritebacksLocations = getLocationsForPmsType('dolphinCloud');
  const openDentalLocalWritebacksLocations = getLocationsForPmsType('openDentalLocal');
  const dolphinWritebacksLocations = getLocationsForPmsType('dolphin');
  const crystalWritebacksLocations = getLocationsForPmsType('crystal');
  const athenaOneWritebacksLocations = getLocationsForPmsType('athenaOne');
  const nextechSelectWritebacksLocations = getLocationsForPmsType('nextechSelect');
  const nextechPracticeWritebacksLocations = getLocationsForPmsType('nextechPractice');
  const dolphinLegacyWritebacksLocations = getLocationsForPmsType('dolphinLegacy');

  return {
    drChronoWritebacksLocations,
    openDentalWritebacksLocations,
    openDentalCloudWritebacksLocations,
    eaglesoftWritebacksLocations,
    fuseWritebacksLocations,
    dentrixEnterpriseWritebacksLocations,
    dolphinCloudWritebacksLocations, // Return Dolphin Cloud locations
    openDentalLocalWritebacksLocations,
    dolphinWritebacksLocations,
    bulkCollectionsEnabledLocations: bulkCollectionsEnabledLocationsFromSources,
    crystalWritebacksLocations,
    athenaOneWritebacksLocations,
    nextechSelectWritebacksLocations,
    nextechPracticeWritebacksLocations,
    dolphinLegacyWritebacksLocations,
  };
};
